// Intent: agent-mode inbox — APP-channel conversations of THIS agent's clients
// (caretaker rule, enforced server-side). Żaneta's core ask: see that a client
// wrote something and answer from the phone. List polls at 20 s while focused
// (mirrors the CRM list cadence); the pane itself polls faster.
// Admin view (auth-store.isAdmin): the SAME screen lists ALL conversations
// (both websites + app) with a source badge and "who is on it" — Wojtek's ask:
// know about every chat from the phone. Authorization stays server-side.

import { router, useFocusEffect } from 'expo-router';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import {
  AgentChatConversation,
  fetchAdminConversationsApi,
  fetchAgentConversationsApi,
} from '@/api/mobile-api';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { ScreenHeader } from '@/components/ui/screen-header';
import { useAuthStore } from '@/store/auth-store';
import { colors, radius, spacing } from '@/theme/tokens';

const LIST_POLL_MS = 20000;

const SITE_LABELS: Record<string, string> = {
  polfinance: 'polfinance.pl',
  kredytowyprzewodnik: 'kredytowyprzewodnik.pl',
};

function timeLabel(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) {
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function AgentChatListScreen() {
  const { t } = useTranslation();
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const [conversations, setConversations] = useState<AgentChatConversation[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      const load = async () => {
        // Admin = the all-chats endpoint; the server re-verifies the role.
        const res = isAdmin ? await fetchAdminConversationsApi() : await fetchAgentConversationsApi();
        if (cancelled) return;
        if (res.ok && res.data) {
          setConversations(res.data);
          setError(null);
        } else {
          setError(res.message ?? t('agentMode.chat.loadError'));
        }
        setLoaded(true);
      };
      void load();
      const interval = setInterval(load, LIST_POLL_MS);
      return () => {
        cancelled = true;
        clearInterval(interval);
      };
    }, [t, isAdmin]),
  );

  const sourceLabel = (c: AgentChatConversation): string | null => {
    if (c.channel === 'APP') return t('agentMode.chat.channelApp');
    return (c.site && SITE_LABELS[c.site]) || null;
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.sm, paddingBottom: spacing.xl }}>
        <ScreenHeader title={isAdmin ? t('agentMode.chat.titleAdmin') : t('agentMode.chat.title')} />

        {error ? (
          <Card style={{ backgroundColor: colors.warningBg }}>
            <AppText variant="bodyLg" color={colors.warning}>
              {error}
            </AppText>
          </Card>
        ) : null}

        {loaded && !error && conversations.length === 0 ? (
          <Card>
            <AppText variant="bodyLg" color={colors.textSecondary}>
              {isAdmin ? t('agentMode.chat.emptyAdmin') : t('agentMode.chat.empty')}
            </AppText>
          </Card>
        ) : null}

        {conversations.map((c) => (
          <Pressable
            key={c.id}
            accessibilityRole="button"
            onPress={() => router.push(`/agent-chat/${c.id}` as never)}
            style={({ pressed }) => ({ transform: [{ scale: pressed ? 0.98 : 1 }] })}
          >
            <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
              {c.unread ? (
                <View
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: radius.full,
                    backgroundColor: colors.error,
                  }}
                  accessibilityLabel={t('agentMode.chat.unread')}
                />
              ) : (
                <Ionicons name="chatbubble-outline" size={18} color={colors.outlineVariant} />
              )}
              <View style={{ flex: 1 }}>
                <AppText variant="headlineSm">{c.visitorName ?? `#${c.id}`}</AppText>
                <AppText variant="bodyMd" color={colors.textSecondary} numberOfLines={1}>
                  {c.lastMessagePreview ?? ''}
                </AppText>
                {isAdmin ? (
                  <AppText variant="labelMd" color={colors.outlineVariant} numberOfLines={1}>
                    {[
                      sourceLabel(c),
                      c.assignedUserName
                        ? t('agentMode.chat.assigned', { name: c.assignedUserName })
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </AppText>
                ) : null}
              </View>
              <AppText variant="labelMd" color={colors.outlineVariant}>
                {timeLabel(c.lastVisitorMsgAt)}
              </AppText>
            </Card>
          </Pressable>
        ))}
      </ScrollView>
    </SafeAreaView>
  );
}
